import {
  Badge,
  Button,
  Card,
  Heading,
  PageShell,
  PanelSection,
  ProgressMeter,
  Text,
} from "@/components/ui";
import { Field, Input } from "@/components/ui/field";

const RAMP = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

const BUTTON_VARIANTS = ["primary", "accent", "secondary", "ghost", "link", "danger"] as const;

const BADGE_TONES = ["neutral", "accent", "success", "warning", "danger", "info"] as const;

function isE2eTestMode() {
  return import.meta.env.VITE_E2E_TEST_MODE === "true";
}

export function DesignSystemPage() {
  if (!isE2eTestMode()) {
    return (
      <main data-testid="not-found" className="flex min-h-screen items-center justify-center p-8">
        <Heading as="h1" level="page">
          Not found
        </Heading>
      </main>
    );
  }

  return (
    <PageShell width="wide" data-testid="token-gallery">
      <Heading as="p" level="eyebrow">
        Astromania
      </Heading>
      <Heading as="h1" level="page" className="mt-1">
        Night Sky &amp; Marigold
      </Heading>
      <Text tone="muted" size="base" className="mt-2 max-w-2xl">
        Every token and primitive variant. Each sample carries its own contrast requirement,
        measured in both themes by the accessibility spec.
      </Text>

      <div className="mt-10 space-y-8">
        <PanelSection title="Ramp" description="Purple-tinted and background-relative.">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {RAMP.map((step) => (
              <div
                key={step}
                className="rounded-lg border border-[var(--border)] p-3 text-xs"
                style={{ background: `var(--ink-${step})` }}
              >
                <span className={step >= 9 ? "text-[var(--ink-1)]" : "text-[var(--ink-12)]"}>
                  ink-{step}
                </span>
              </div>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Text roles" description="Held to 4.5:1 on every surface.">
          <div className="grid gap-3 sm:grid-cols-3">
            {(["base", "sunken"] as const).map((tone) => (
              <Card key={tone} tone={tone} pad="base">
                <Text data-contrast-sample={`text-primary on ${tone}`}>Primary text</Text>
                <Text
                  tone="secondary"
                  className="mt-1"
                  data-contrast-sample={`text-secondary on ${tone}`}
                >
                  Secondary text
                </Text>
                <Text tone="muted" className="mt-1" data-contrast-sample={`text-muted on ${tone}`}>
                  Muted text
                </Text>
              </Card>
            ))}
            <Card tone="dashed" pad="base">
              <Text tone="muted" data-contrast-sample="text-muted on dashed">
                Empty state
              </Text>
            </Card>
          </div>
        </PanelSection>

        <PanelSection
          title="Buttons"
          description="Each fill ships with the foreground that reads on it."
        >
          <div className="flex flex-wrap items-center gap-3">
            {BUTTON_VARIANTS.map((variant) => (
              <Button key={variant} variant={variant} data-contrast-sample={`button:${variant}`}>
                {variant}
              </Button>
            ))}
            <Button variant="accent" pill data-contrast-sample="button:accent-pill">
              Enrol now
            </Button>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Close"
              data-contrast-sample="button:icon"
            >
              ×
            </Button>
            <Button variant="primary" disabled>
              Disabled
            </Button>
          </div>
        </PanelSection>

        <PanelSection title="Badges" description="Colour never carries meaning alone.">
          <div className="flex flex-wrap gap-2">
            {BADGE_TONES.map((tone) => (
              <Badge key={tone} tone={tone} data-contrast-sample={`badge:${tone}`}>
                {tone}
              </Badge>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Form field" description="Label, hint and error wired once.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" hint="We never share this." required>
              <Input type="email" placeholder="you@example.com" />
            </Field>
            <Field label="Password" error="That password is too short.">
              <Input type="password" />
            </Field>
          </div>
        </PanelSection>

        <PanelSection title="Progress" description="Exposed to assistive tech, not just painted.">
          <ProgressMeter value={12} max={90} label="Course progress: 12 of 90 sessions" />
          <Text tone="muted" className="mt-2" data-contrast-sample="progress-label">
            12 / 90 sessions
          </Text>
        </PanelSection>

        <PanelSection title="Typography" description="Literata for display, Poppins for UI.">
          <Heading as="p" level="display">
            Display
          </Heading>
          <Heading as="p" level="page" className="mt-2">
            Page title
          </Heading>
          <Heading as="p" level="section" className="mt-2">
            Section
          </Heading>
          <Heading as="p" level="card" className="mt-2">
            Card title
          </Heading>
          <Heading as="p" level="eyebrow" className="mt-3" data-contrast-sample="eyebrow">
            Eyebrow
          </Heading>
        </PanelSection>
      </div>
    </PageShell>
  );
}
